import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { get } from "../api";
import type { MetricCampaign, MetricsPage, PeriodsPage } from "../api";
import { useCustomer } from "./CustomerLayout";
import Trend from "../metrics/Trend";
import Kpis from "../metrics/Kpis";
import Signals from "../metrics/Signals";
import CampaignTable from "../metrics/CampaignTable";
import PeriodTable from "../metrics/PeriodTable";
import CampaignFilter from "../metrics/CampaignFilter";
import { addDays, firstOfMonth, isIsoDate, spanDays, todayIst } from "../metrics/calendar";

/** Windows worth offering. 4 weeks is the default because it is the span a
 *  baseline compares against, so the page shows what a detector reasons over. */
const PRESETS = [
  { key: "7d", label: "7 days" },
  { key: "28d", label: "4 weeks" },
  { key: "90d", label: "90 days" },
  { key: "mtd", label: "Month to date" },
  { key: "custom", label: "Custom" },
] as const;
type Preset = (typeof PRESETS)[number]["key"];

/** The server's own limit; asking past it is a 400. */
const MAX_DAYS = 366;

/**
 * The window the URL describes.
 *
 * `?w=7d|28d|90d|mtd`, or `?w=custom&from=&to=`. The older `?d=<days>` is
 * still read, so a link somebody saved keeps opening the window it named.
 * Dates are the business calendar's (IST), the same "today" the server
 * uses for its weeks and months.
 */
function windowFrom(params: URLSearchParams): {
  preset: Preset;
  from: string;
  to: string;
  invalid: string | null;
} {
  const today = todayIst();
  const legacy = params.get("d");
  const w = (params.get("w") ?? (legacy ? `${legacy}d` : "28d")) as Preset | string;
  const days = /^(\d+)d$/.exec(w);

  if (w === "mtd") return { preset: "mtd", from: firstOfMonth(today), to: today, invalid: null };
  if (w === "custom") {
    const from = params.get("from");
    const to = params.get("to");
    if (!isIsoDate(from) || !isIsoDate(to)) {
      return { preset: "custom", from: addDays(today, -27), to: today, invalid: "Pick both dates." };
    }
    let invalid: string | null = null;
    if (from > to) invalid = "The start date is after the end date.";
    else if (to > today) invalid = "The end date is in the future.";
    else if (spanDays(from, to) > MAX_DAYS) invalid = `A window can be at most ${MAX_DAYS} days.`;
    return { preset: "custom", from, to, invalid };
  }
  const n = days ? Math.min(Math.max(Number(days[1]), 1), MAX_DAYS) : 28;
  const preset = (PRESETS.find((p) => p.key === `${n}d`)?.key ?? "28d") as Preset;
  return { preset, from: addDays(today, -(n - 1)), to: today, invalid: null };
}

/**
 * One customer's platform metrics.
 *
 * Reads the mirrored rollup, never Redshift (invariant 38) — so this page
 * renders in milliseconds and keeps rendering while global's zero-ETL
 * integration is resyncing and the warehouse is answering nothing.
 *
 * Two halves, and the split is the point. The top — what needs attention,
 * and the weeks-and-months comparison — is anchored on today and ignores
 * the window picker: zooming a chart must not make an alert vanish or move
 * the week being compared. The bottom — tiles, charts, campaigns — is the
 * picked window.
 *
 * The campaign filter lives in the URL so a view can be linked, and narrows
 * both halves except the signals. It narrows within the caller's scope and
 * cannot widen it: the server joins the customer's domains into every
 * statement, so an id belonging to somebody else matches nothing rather
 * than erroring.
 */
export default function CustomerMetrics() {
  const { customer } = useCustomer();
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState<MetricsPage | null>(null);
  const [periods, setPeriods] = useState<PeriodsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [periodsError, setPeriodsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [campaignList, setCampaignList] = useState<MetricCampaign[]>([]);

  const { preset, from, to, invalid } = windowFrom(params);
  // Comma-separated ids in the URL, so a filtered view is a link. Absent
  // is every campaign.
  const campaign = params.get("campaign") || null;
  const selectedIds = campaign ? campaign.split(",") : null;

  const update = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(params);
      next.delete("d");
      for (const [key, value] of Object.entries(changes)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const choose = (key: Preset) => {
    if (key === "custom") {
      // Start the custom range on whatever was showing, so switching to it
      // does not throw the view away.
      update({ w: "custom", from, to });
    } else {
      update({ w: key, from: null, to: null });
    }
  };

  useEffect(() => {
    if (invalid) return;
    let live = true;
    // The previous numbers stay on screen, dimmed, while the next ones
    // load: blanking the page would also unmount the controls somebody is
    // in the middle of using.
    setLoading(true);
    setError(null);
    const q = new URLSearchParams({ from, to });
    if (campaign) q.set("campaign", campaign);

    void get<MetricsPage>(`/api/customers/${customer.name}/metrics?${q}`)
      .then((p) => live && setPage(p))
      .catch((e) => live && setError(e.message))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [customer.name, from, to, campaign, invalid]);

  // What the filter offers. Once per customer: it does not change with the
  // window or with what is selected.
  useEffect(() => {
    let live = true;
    // A different customer: nothing on screen belongs to them.
    setCampaignList([]);
    setPage(null);
    setPeriods(null);
    void get<{ campaigns: MetricCampaign[] }>(`/api/customers/${customer.name}/metrics/campaigns`)
      .then((r) => live && setCampaignList(r.campaigns))
      .catch(() => live && setCampaignList([]));
    return () => {
      live = false;
    };
  }, [customer.name]);

  // Separate from the series: it does not depend on the window, so
  // changing the picker does not refetch it.
  useEffect(() => {
    let live = true;
    setPeriodsError(null);
    const q = new URLSearchParams();
    if (campaign) q.set("campaign", campaign);
    void get<PeriodsPage>(`/api/customers/${customer.name}/metrics/periods?${q}`)
      .then((p) => live && setPeriods(p))
      .catch((e) => live && setPeriodsError(e.message));
    return () => {
      live = false;
    };
  }, [customer.name, campaign]);

  const picker = (
    <div className="controls">
      <div className="picker" role="group" aria-label="Window">
        {PRESETS.map((p) => (
          <button key={p.key} className={p.key === preset ? "on" : ""} onClick={() => choose(p.key)}>
            {p.label}
          </button>
        ))}
      </div>
      {preset === "custom" && (
        <div className="range">
          <input
            type="date"
            aria-label="From"
            value={isIsoDate(params.get("from")) ? from : ""}
            max={to}
            onChange={(e) => update({ w: "custom", from: e.target.value || null })}
          />
          <span className="dim">to</span>
          <input
            type="date"
            aria-label="To"
            value={isIsoDate(params.get("to")) ? to : ""}
            min={from}
            max={todayIst()}
            onChange={(e) => update({ w: "custom", to: e.target.value || null })}
          />
        </div>
      )}
      {!invalid && <span className="sub dim">{spanDays(from, to)} days</span>}
    </div>
  );

  if (invalid) {
    return (
      <>
        {picker}
        <div className="error">{invalid}</div>
      </>
    );
  }
  if (error) return <div className="error">{error}</div>;
  if (!page) return <div className="empty">Loading…</div>;

  // No tenants mapped is a different statement from no traffic, and saying
  // so is the difference between "nobody set this up" and "everything
  // stopped". The second would send somebody looking for an outage.
  if (!page.has_platform_domains) {
    return (
      <div className="empty">
        <p>No platform tenants are mapped to {customer.display_name}.</p>
        <p className="sub">
          An admin can attach them under Admin → Platform domains. Until then
          there is nothing to measure — which is not the same as measuring
          nothing.
        </p>
      </div>
    );
  }

  return (
    <div className={loading ? "metrics loading" : "metrics"}>
      <Signals signals={page.signals} days={page.signal_days} />

      {campaignList.length > 0 && (
        <div className="controls filterbar">
          <span className="sub">Showing</span>
          <CampaignFilter
            campaigns={campaignList}
            selected={selectedIds}
            onChange={(ids) => update({ campaign: ids ? ids.join(",") : null })}
          />
          <span className="sub dim">— applies to everything below</span>
        </div>
      )}

      <h2>Weeks and months</h2>
      {periodsError ? (
        <div className="error">{periodsError}</div>
      ) : periods ? (
        <PeriodTable page={periods} />
      ) : (
        <div className="empty">Loading…</div>
      )}

      <h2>Window</h2>
      {picker}
      <Kpis summary={page.totals} />
      <Trend days={page.days} totals={page.totals} />
      <CampaignTable
        campaigns={page.campaigns}
        selected={selectedIds?.length === 1 ? selectedIds[0] : null}
        onSelect={(id) => update({ campaign: id })}
      />

      <p className="sub dim">
        {page.from} to {page.to}. Days are bucketed in each campaign's own timezone; "today",
        weeks and months follow the IST calendar. Test traffic is excluded.
      </p>
    </div>
  );
}
